// use-delete-team.ts — Teams suite, action
//
// DELETE /api/teams/:teamId. Removes the team's detail cache entry and invalidates
// the teams list.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { teamsDetailKey, teamsRootKey } from './team-query-keys';

export function useDeleteTeam(): ActionResult<{ teamId: string }, void> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useActionHook<{ teamId: string }, void>(
    useMemo(
      () => ({
        mutationFn: async ({ teamId }) => {
          await api.teams.byId(teamId).delete();
        },
        onInvalidate: (_data, { teamId }) => {
          queryClient.removeQueries({ queryKey: teamsDetailKey(teamId) });
          queryClient.invalidateQueries({ queryKey: teamsRootKey });
        },
      }),
      [api, queryClient],
    ),
  );
}
